import { useCallback, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { processError } from "../../../../common/utils/base_error";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { PermissaoAcessoGrupoLista } from "../../domain/models/permissao_acesso_grupo_lista";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { PermissaoCadastro } from "../../domain/models/permissao_cadastro";
import { useGridApiRef, gridRowSelectionIdsSelector } from "@mui/x-data-grid-pro";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { ReportLogAcessoSistemaForm } from "../schemas/reports/logAcessoSistemaReport";

/**
 * usePermissaoAcessoCadastroViewModel
 */



export const usePermissaoAcessoCadastroViewModel = () => {
  const {
    reports: {
      getReportLogAcessoSistemaUseCase,
      getReportPermissaoAcessoUseCase,
    },
    administracao: {
      createPermissaoGrupoAcessoUseCase,
      getAllPermissaoGrupoAcessoUseCase,
    },
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
      getGrupoAcessoDropDownUseCase,
      getUsuarioDropDownUseCase,
    }
  } = useDependencyInjection();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });


  const menuRef = useRef<HTMLButtonElement>(null);
  const [openMenu, setOpenMenu] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { pushErrorNotification, pushWarnNotification, pushInfoNotification, setPageLoading } = usePageLoader();

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  //Relatórios
  const [openReportLogAcessoSistemaModal, setOpenReportLogAcessoSistemaModal] = useState(false);
  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  const [ddlUsuarios, setDdlUsuarios] = useState<DropDown[]>([]);


  //Dados para cadastro das permissões
  const permissaoCadastroLista = useRef<PermissaoCadastro[]>([]);
  const apiRef = useGridApiRef();

  // const [permissaoGrupoSelectionModel, setPermissaoGrupoSelectionModel] =
  //   useState<GridRowSelectionModel>();

  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [selectedIdGrupoAcesso, setSelectedIdGrupoAcesso] = useState<number>();
  const [ddlGrupoAcesso, setDdlGrupoAcesso] = useState<DropDown[]>([]);

  const [grupoAcessoCarregamento, setGrupoAcessoCarregamento] = useState(0);

  // Lista de permissões do grupo de acesso, para popular a tabela.
  const [permissaoAcessoGrupoLista, setPermissaoAcessoGrupoLista] = useState<PermissaoAcessoGrupoLista[]>([])



  //#region CARREGAMENTO

  const preenchimentoPermissaoCadastro = useCallback((permissoes: PermissaoAcessoGrupoLista[]) => {
    const listaPermissaoCadastro: PermissaoCadastro[] = permissoes.map((permissao) => ({
      idTela: Number(permissao.id.replace('T-', '')),
      idGrupoAcesso: selectedIdGrupoAcesso ?? 0,
      checked: permissao.checked,
      desabilitaCriacao: permissao.criacao !== undefined ? permissao.criacao : undefined,
      desabilitaExclusao: permissao.excluir !== undefined ? permissao.excluir : undefined,
      desabilitaEdicao: permissao.editar !== undefined ? permissao.editar : undefined,
      desabilitaRelatorio: permissao.relatorio !== undefined ? permissao.relatorio : undefined,
      desabilitaStatus: permissao.status !== undefined ? permissao.status : undefined,
      desabilitaExclusaoDoc: permissao.excluirDoc !== undefined ? permissao.excluirDoc : undefined,
      desabilitaAprovacaoLote: permissao.aprovarLote !== undefined ? permissao.aprovarLote : undefined,
      desabilitaExclusaoLote: permissao.excluirLote !== undefined ? permissao.excluirLote : undefined,
    }));

    permissaoCadastroLista.current = listaPermissaoCadastro;

  }, [
    selectedIdGrupoAcesso
  ]);


  const loadPermissoesAcessoGrupo = useCallback(async (idGrupoAcesso: number) => {
    setLoading(true);
    try {
      const permissoes = await getAllPermissaoGrupoAcessoUseCase.execute(idGrupoAcesso);
      setPermissaoAcessoGrupoLista(permissoes);

      await preenchimentoPermissaoCadastro(permissoes);

      if (permissoes.length > 0) {
        apiRef.current?.setRowSelectionModel(
          { type: 'include', ids: new Set(permissoes.filter(p => p.checked).map(p => p.id)) }
        );
      }

    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar permissões de acesso do grupo!'), { isAutoFinish: true });
    } finally {
      setLoading(false);
    }
  }, [
    apiRef,
    getAllPermissaoGrupoAcessoUseCase,
    pushErrorNotification,
    preenchimentoPermissaoCadastro,
  ]);

  const loadUsuariosDropdown = useCallback(async () => {
    setPageLoading(true);
    try {
      const ddlUsuarios = await getUsuarioDropDownUseCase.getUsuarioDropdown();
      setDdlUsuarios(ddlUsuarios.map(u => ({ id: u.idUsuario, title: u.nome })));
    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar usuários!'), { isAutoFinish: true });
    } finally {
      setPageLoading(false);
    }
  }, [
    getUsuarioDropDownUseCase,
    pushErrorNotification,
    setPageLoading,
  ]);


  const loadGrupoAcessoDropdown = useCallback(async () => {
    setPageLoading(true);
    try {
      const ddlGrupoAcesso = await getGrupoAcessoDropDownUseCase.getGrupoAcessoDropDown();
      setDdlGrupoAcesso(ddlGrupoAcesso);
    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar grupo de acesso!'), { isAutoFinish: true });
    } finally {
      setPageLoading(false);
    }
  }, [
    getGrupoAcessoDropDownUseCase,
    pushErrorNotification,
    setPageLoading,
  ]);



  //#endregion CARREGAMENTO




  //#region CABEÇALHO

  const handlePageBack = useCallback(() => {
    navigate('/GruposAcesso_lista');
  }, [navigate]);

  const handleReload = useCallback(async () => {
    try {

    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar grupo de acesso!'), { isAutoFinish: true });
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCloseMenu = useCallback((event: any) => {
    if (
      menuRef.current &&
      menuRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpenMenu(false);
  }, []);

  const handleToggleMenu = useCallback(() => {
    setOpenMenu((prev) => !prev);
  }, []);

  const handleListKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpenMenu(false);
    } else if (event.key === 'Escape') {
      setOpenMenu(false);
    }
  }, []);

  //#endregion





  //#region GRUPO DE ACESSO CHANGE

  const handleGrupoAcessoChange = useCallback(async (idGrupoAcesso: number | null) => {
    if (idGrupoAcesso !== null) {
      setSelectedIdGrupoAcesso(idGrupoAcesso);
      await loadPermissoesAcessoGrupo(idGrupoAcesso);

    } else {
      setSelectedIdGrupoAcesso(undefined);
      setPermissaoAcessoGrupoLista([]);
    }

    requestAnimationFrame(() => {
      setGrupoAcessoCarregamento((prev) => prev + 1);
    });

  }, [
    loadPermissoesAcessoGrupo,
    setSelectedIdGrupoAcesso,
    setPermissaoAcessoGrupoLista,
  ]);

  //#endregion



  //#region PAGE START
  usePageStartBootstrap(async () => {
    try {
      await loadGrupoAcessoDropdown();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao carregar permissões de acesso do grupo!'), { isAutoFinish: true });
    }
  });


  //#endregion


  //#region CADASTRO PERMISSÕES


  const handleSubmitCadastroPermissoes = useCallback(async () => {
    setSaving(true);
    try {
      if (selectedIdGrupoAcesso === undefined) {
        pushWarnNotification("Atenção", "Selecione um grupo de acesso para salvar as permissões.", { isAutoFinish: true });
        return;
      }

      var listaSet = new Set<string>();

      //POPULAR ID DOS SELECIONADOS.
      Array.from(
        gridRowSelectionIdsSelector(apiRef).keys(),
        id => listaSet.add(String(id))
      );

      //POPULAR IDS DOS PAIS DOS SELECIONADOS.
      permissaoAcessoGrupoLista.map(item => {
        if (listaSet.has(item.id) && item.idPai) {
          listaSet.add(item.idPai);
        }
      });

      const selectedIds = Array.from(listaSet);
      const teste = selectedIds.map(id => {
        if (id.startsWith('T-')) {
          return Number(id.replace('T-', ''));
        } else {
          return NaN;
        }
      });

      const normalizedPermissaoCadastro = permissaoCadastroLista.current
        .filter((item) => !Number.isNaN(item.idTela) && teste.includes(item.idTela));

      await createPermissaoGrupoAcessoUseCase.execute(normalizedPermissaoCadastro, selectedIdGrupoAcesso);
      await loadPermissoesAcessoGrupo(selectedIdGrupoAcesso);
      pushInfoNotification("Sucesso", "Permissões salvas com sucesso!", { isAutoFinish: true });

    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao criar permissões!'), { isAutoFinish: true });
    } finally {
      setSaving(false);
    }
  }, [
    createPermissaoGrupoAcessoUseCase,
    selectedIdGrupoAcesso,
    permissaoAcessoGrupoLista,
    apiRef,
    loadPermissoesAcessoGrupo,
    pushWarnNotification,
    pushInfoNotification,
    pushErrorNotification
  ]);


  //#endregion



  //#region FUNÇÕES CONTROLE

  const handleDesabilitarCriacaoChange = useCallback((idTela: string, newValue: boolean) => {
    const idNormalizado = Number(idTela.replace('T-', ''));
    const indice = permissaoCadastroLista.current.findIndex(item => item.idTela === idNormalizado);
    if (indice !== -1) {
      permissaoCadastroLista.current[indice].desabilitaCriacao = newValue;
    }

    apiRef.current?.updateRows([{ id: idTela, criacao: newValue }]);

  }, [apiRef]);

  const handleDesabilitarExclusaoChange = useCallback((idTela: string, newValue: boolean) => {
    const idNormalizado = Number(idTela.replace('T-', ''));
    const indice = permissaoCadastroLista.current.findIndex(item => item.idTela === idNormalizado);

    if (indice !== -1) {
      permissaoCadastroLista.current[indice].desabilitaExclusao = newValue;
    }
    apiRef.current?.updateRows([{ id: idTela, excluir: newValue }]);

  }, [apiRef]);

  const handleDesabilitarEdicaoChange = useCallback((idTela: string, newValue: boolean) => {
    const idNormalizado = Number(idTela.replace('T-', ''));
    const indice = permissaoCadastroLista.current.findIndex(item => item.idTela === idNormalizado);

    if (indice !== -1) {
      permissaoCadastroLista.current[indice].desabilitaEdicao = newValue;
    }

    apiRef.current?.updateRows([{ id: idTela, editar: newValue }]);

  }, [apiRef]);

  const handleDesabilitarRelatorioChange = useCallback((idTela: string, newValue: boolean) => {
    const idNormalizado = Number(idTela.replace('T-', ''));
    const indice = permissaoCadastroLista.current.findIndex(item => item.idTela === idNormalizado);
    if (indice !== -1) {
      permissaoCadastroLista.current[indice].desabilitaRelatorio = newValue;
    }

    apiRef.current?.updateRows([{ id: idTela, relatorio: newValue }]);

  }, [apiRef]);

  const handleDesabilitarAlteracaoStatusChange = useCallback((idTela: string, newValue: boolean) => {
    const idNormalizado = Number(idTela.replace('T-', ''));
    const indice = permissaoCadastroLista.current.findIndex(item => item.idTela === idNormalizado);
    if (indice !== -1) {
      permissaoCadastroLista.current[indice].desabilitaStatus = newValue;
    }

    apiRef.current?.updateRows([{ id: idTela, status: newValue }]);

  }, [apiRef]);

  const handleDesabilitarExclusaoDocChange = useCallback((idTela: string, newValue: boolean) => {
    const idNormalizado = Number(idTela.replace('T-', ''));
    const indice = permissaoCadastroLista.current.findIndex(item => item.idTela === idNormalizado);
    if (indice !== -1) {
      permissaoCadastroLista.current[indice].desabilitaExclusaoDoc = newValue;
    }

    apiRef.current?.updateRows([{ id: idTela, excluirDoc: newValue }]);

  }, [apiRef]);

  const handleDesabilitarExclusaoLoteChange = useCallback((idTela: string, newValue: boolean) => {
    const idNormalizado = Number(idTela.replace('T-', ''));
    const indice = permissaoCadastroLista.current.findIndex(item => item.idTela === idNormalizado);
    if (indice !== -1) {
      permissaoCadastroLista.current[indice].desabilitaExclusaoLote = newValue;
    }

    apiRef.current?.updateRows([{ id: idTela, excluirLote: newValue }]);

  }, [apiRef]);

  const handleDesabilitarAprovacaoLoteChange = useCallback((idTela: string, newValue: boolean) => {
    const idNormalizado = Number(idTela.replace('T-', ''));
    const indice = permissaoCadastroLista.current.findIndex(item => item.idTela === idNormalizado);
    if (indice !== -1) {
      permissaoCadastroLista.current[indice].desabilitaAprovacaoLote = newValue;
    }

    apiRef.current?.updateRows([{ id: idTela, aprovarLote: newValue }]);

  }, [apiRef]);


  //#endregion






  //#region HISTORICO

  const handleOpenChangeHistory = useCallback((
    idTabela: number | null,
    tipoTabela: LogTipoTabela | null,
  ) => {
    if (idTabela && tipoTabela) {
      changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
    } else {
      if (true) {
        changeHistory.handleOpenChangeHistory(selectedIdGrupoAcesso ?? 0, LogTipoTabela.GRUPO_ACESSO, true);
      }
    }
  }, [
    selectedIdGrupoAcesso,
    changeHistory,
  ]);

  //#endregion

  //#region RELATÓRIOS

  const handleOpenReportLogAcessoSistemaModal = useCallback(async (openState: boolean) => {

    setOpenReportLogAcessoSistemaModal(openState);
    try {
      setLoading(true);
      if (openState) {
        await loadUsuariosDropdown();
        setOpenReportLogAcessoSistemaModal(openState);
      } else {
        setOpenReportLogAcessoSistemaModal(openState);
      }

    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao carregar filtros do relatório!'));
    }
    finally {
      setLoading(false);
    }
  }, [
    loadUsuariosDropdown,
    pushErrorNotification,
  ]);

  const handlePermissaoAcessoExportar = useCallback(async () => {
    try {
      setLoading(true);
      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) => {
          return await getReportPermissaoAcessoUseCase.execute(requiredMimeType);
        }
      });

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao gerar relatório!'));
    } finally {
      setLoading(false);
    }
  }, [
    getReportPermissaoAcessoUseCase,
    pushErrorNotification,
    startReportViewerProcess
  ]);


  const handleLogAcessoSistemaReportSubmit = useCallback(async (form: ReportLogAcessoSistemaForm) => {
    try {
      setLoading(true)
      await startReportViewerProcess({
        fetchCallback: async (mimeType) => {
          return await getReportLogAcessoSistemaUseCase.execute(
            mimeType,
            form.idUsuario,
            form.dataDe ?? null,
            form.dataAte ?? null,
          );
        }
      });
    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao carregar relatório!'));

    } finally {
      setLoading(false);
    }
  }, [
    getReportPermissaoAcessoUseCase,
    pushErrorNotification,
    startReportViewerProcess
  ]);


  //#endregion




  return ({
    loading,
    errorMessage,
    saving,
    permissaoAcessoGrupoLista,
    changeHistory,
    menuRef,
    openMenu,
    ddlGrupoAcesso: ddlGrupoAcesso ?? [],
    selectedIdGrupoAcesso,
    apiRef,
    reportViewerState,
    ddlUsuarios,
    openReportLogAcessoSistemaModal,

    handleLogAcessoSistemaReportSubmit,
    handlePermissaoAcessoExportar,
    handleOpenReportLogAcessoSistemaModal,
    handleSubmitCadastroPermissoes,
    handleGrupoAcessoChange,
    handleCloseMenu,
    handleToggleMenu,
    handleListKeyDown,
    handlePageBack,
    handleReload,
    handleOpenChangeHistory,
    handleDesabilitarCriacaoChange,
    handleDesabilitarExclusaoChange,
    handleDesabilitarEdicaoChange,
    handleDesabilitarRelatorioChange,
    handleDesabilitarAlteracaoStatusChange,
    handleDesabilitarExclusaoDocChange,
    handleDesabilitarExclusaoLoteChange,
    handleDesabilitarAprovacaoLoteChange,

  });
}